import {computed, type Ref} from 'vue';
import type {FactoryArg, FactoryOpts} from 'imask';
import {useTranslation} from '#imports';
import type {MaltInputMaskPreset} from '../MaltInputMask.vue';

interface NumberMaskOptions {
    preset: Ref<MaltInputMaskPreset | undefined>;
    maskOptions: Ref<Partial<FactoryArg>>;
}

/**
 * Regroups all the number mask options
 *
 * Returns the correct mask options based on the preset
 *
 * @param preset - The preset to use
 * @param maskOptions - The mask options to use
 */
export function useNumberMaskOptions({preset, maskOptions}: NumberMaskOptions) {
    const {t} = useTranslation();

    const commonNumberOptions: FactoryOpts = {
        mask: Number,
        autofix: true,
        radix: t('generic.form.number.radix'),
        mapToRadix: ['.', ','],
        normalizeZeros: false,
        thousandsSeparator: t('generic.form.number.thousands.separator.delimiter'),
    };

    const numberMaskOptions = computed<FactoryOpts>(() => {
        if (preset.value === 'integer') {
            // Handles integer input (no decimals) Ex: 1000 -> 1 000
            return {
                scale: 0,
                ...commonNumberOptions,
                ...(maskOptions.value ?? {}),
            };
        } else if (preset.value === 'price') {
            // Handles price input (2 decimals) Ex: 10.00 -> 10,00
            return {
                scale: 2,
                min: null,
                ...commonNumberOptions,
                ...(maskOptions.value ?? {}),
            };
        } else {
            return {};
        }
    });

    return {
        numberMaskOptions,
    };
}
